import { useState } from "react";
import { Mail, Instagram, MessageCircle, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [sending, setSending] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    setSending(true);
    setTimeout(() => {
      setSending(false);
      toast.success("Thank you \u2014 we\u2019ll write back within 24 hours.");
      e.target.reset();
    }, 700);
  };

  return (
    <div data-testid="contact-page" className="bg-[#FAF8F5]">
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pt-16 md:pt-24 pb-16">
        <p className="overline">Say hello</p>
        <h1 className="mt-6 font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight leading-none text-[#2A2626]">
          We&rsquo;d love to hear<br />
          <span className="italic font-light">from you.</span>
        </h1>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Form */}
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 space-y-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="overline">First name</label>
                <input
                  data-testid="contact-first-name"
                  required
                  name="firstName"
                  className="mt-3 w-full bg-transparent border-b border-[#DCD5CA] py-3 focus:outline-none focus:border-[#2A2626] font-sans text-[#2A2626]"
                />
              </div>
              <div>
                <label className="overline">Last name</label>
                <input
                  data-testid="contact-last-name"
                  name="lastName"
                  className="mt-3 w-full bg-transparent border-b border-[#DCD5CA] py-3 focus:outline-none focus:border-[#2A2626] font-sans text-[#2A2626]"
                />
              </div>
            </div>
            <div>
              <label className="overline">Email</label>
              <input
                data-testid="contact-email"
                required
                type="email"
                name="email"
                className="mt-3 w-full bg-transparent border-b border-[#DCD5CA] py-3 focus:outline-none focus:border-[#2A2626] font-sans text-[#2A2626]"
              />
            </div>
            <div>
              <label className="overline">Subject</label>
              <input
                data-testid="contact-subject"
                name="subject"
                className="mt-3 w-full bg-transparent border-b border-[#DCD5CA] py-3 focus:outline-none focus:border-[#2A2626] font-sans text-[#2A2626]"
              />
            </div>
            <div>
              <label className="overline">Message</label>
              <textarea
                data-testid="contact-message"
                required
                name="message"
                rows={5}
                className="mt-3 w-full bg-transparent border-b border-[#DCD5CA] py-3 focus:outline-none focus:border-[#2A2626] font-sans text-[#2A2626] resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="inline-block bg-[#2A2626] text-[#FAF8F5] px-10 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-[#3A3632] transition-colors disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send message"}
            </button>
          </form>

          {/* Info */}
          <aside className="lg:col-span-5 lg:pl-8">
            <div className="border-t border-[#DCD5CA] pt-8 space-y-8">
              <div>
                <p className="overline">Care &amp; orders</p>
                <a
                  href="mailto:innayaaofficial@gmail.com"
                  data-testid="contact-email-link"
                  className="mt-3 flex items-center gap-3 font-serif text-2xl text-[#2A2626] hover:opacity-70 break-all"
                >
                  <Mail size={18} strokeWidth={1.5} className="shrink-0" />
                  innayaaofficial@gmail.com
                </a>
              </div>
              <div>
                <p className="overline">Chat with us</p>
                <a
                  href="https://wa.me/918240044327"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-link"
                  className="mt-3 flex items-center gap-3 font-serif text-2xl text-[#2A2626] hover:opacity-70"
                >
                  <MessageCircle size={18} strokeWidth={1.5} className="shrink-0" />
                  WhatsApp — +91 82400 44327
                </a>
              </div>
              <div>
                <p className="overline">Call us</p>
                <a
                  href="tel:+918240044327"
                  data-testid="contact-phone-link"
                  className="mt-3 flex items-center gap-3 font-serif text-2xl text-[#2A2626] hover:opacity-70"
                >
                  <Phone size={18} strokeWidth={1.5} className="shrink-0" />
                  +91 82400 44327
                </a>
                <p className="mt-2 text-[11px] uppercase tracking-[0.22em] text-[#5C564F]">
                  Mon–Sat · 10 am to 6 pm IST
                </p>
              </div>
              <div>
                <p className="overline">Follow along</p>
                <a
                  href="https://www.instagram.com/innayaa.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram-link"
                  className="mt-3 flex items-center gap-3 font-serif text-2xl text-[#2A2626] hover:opacity-70"
                >
                  <Instagram size={18} strokeWidth={1.5} className="shrink-0" />
                  @innayaa.in
                </a>
              </div>
              <div className="pt-6 border-t border-[#DCD5CA]">
                <p className="overline">Studio</p>
                <p className="mt-3 flex items-start gap-3 font-sans text-[15px] text-[#2A2626]">
                  <MapPin size={18} strokeWidth={1.5} className="mt-1 shrink-0" />
                  Handcrafted lovingly in India<br />
                  <span className="text-[#5C564F]">Visits by appointment only</span>
                </p>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
